import React from "react";
import { Box, Chip, Divider, Typography } from "@mui/material";
import DialogLayout from "../../../../Components/DialogLayout";
import { MlmTreeNode } from "../../../../Objects/interfaces/MlmTypes";
import { useTailwindBrand } from "../../../../Components/shared";
import { formatEuro, mlmLevelColor, mlmNodeFullName, sponsorshipStatusDisplay } from "./mlmUtils";

/**
 * Fiche détail d'un membre du réseau, ouverte depuis l'arbre ou le tableau.
 * C'est ici que vivent les chiffres "depuis le début" : les vues principales
 * ne montrent que l'essentiel du mois en cours.
 */
const MlmMemberDetailDialog = ({
  node,
  open,
  onClose,
}: {
  node: MlmTreeNode | null;
  open: boolean;
  onClose: () => void;
}) => {
  const { brandConfig } = useTailwindBrand();
  const fontFamily = brandConfig.fontFamily;

  if (!node) return null;

  const color = mlmLevelColor(node.level);
  const statusDisplay = sponsorshipStatusDisplay(node.statut);

  const statRow = (label: string, value: React.ReactNode, highlight = false) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.25 }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontFamily }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={highlight ? 700 : 600} sx={{ fontFamily, color: highlight ? color : "text.primary" }}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <DialogLayout
      title={mlmNodeFullName(node)}
      open={open}
      handleClose={onClose}
      handleCloseIcon={onClose}
      customDialogActions="none"
    >
      <Box sx={{ fontFamily }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          <Chip size="small" label={`Niveau ${node.level} · ${node.rate ?? "?"} %`} sx={{ bgcolor: color, color: "white", fontWeight: 600 }} />
          {node.teachr.invitationCode && (
            <Chip size="small" variant="outlined" label={node.teachr.invitationCode} sx={{ fontFamily: "monospace" }} />
          )}
          {statusDisplay && (
            <Chip size="small" variant="outlined" label={statusDisplay.title} className={statusDisplay.color} />
          )}
        </Box>

        <Box sx={{ p: 2, borderRadius: 2, bgcolor: `${color}0d`, border: "1px solid", borderColor: `${color}40`, mb: 2 }}>
          {statRow("Votre commission ce mois-ci", formatEuro(node.stats.commissionMonth), true)}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ fontFamily, textTransform: "uppercase", letterSpacing: 0.5 }}>
          Depuis le début
        </Typography>
        <Box sx={{ mt: 0.5 }}>
          {statRow("Commission totale perçue", formatEuro(node.stats.commissionTotal))}
          <Divider />
          {statRow("Chiffre d'affaires commissionné (HT)", formatEuro(node.stats.commissionBase ?? node.stats.feesHt))}
          <Divider />
          {statRow("Cours déclarés", node.stats.coursesCount)}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2, fontFamily }}>
          Votre commission correspond à {node.rate ?? "?"} % du chiffre d'affaires HT des cours déclarés par ce coach.
        </Typography>
      </Box>
    </DialogLayout>
  );
};

export default MlmMemberDetailDialog;
